import "./polyfill";
import "./public-path";

import {
    HeadingAssignUsersToWorkflowStages as AssignUsersToWorkflowStages, 
    Comments,
    DashboardWidget, 
    ErrorBoundary, 
    GroupDetail, 
    GroupsList, 
    LoginModal, 
    DocumentNextWorkflowStageButton as NextWorkflowStageButton,
    NotificationSettings,
    NotificationsDrawer,
    Provider,
    UserAccount,
    UserList,
    WorkflowEditor,
    WorkflowQuickLink,
    WorkflowTasks,
    WorkflowWidget,
    OpenDocumentSettingsButton,
    MaintenanceEditor
} from "partscatalog";

import ReactDOM from "react-dom";
import React from "react";

import AppManager from "./appManager";
import ContextWrapper from "./contextWrapper";
import { store } from "../../partscatalog/src/globals/store";

if (window?.app?.config?.v3MockApiEnabled){
    const { worker } = require("Apimocks/browser");
    worker.start();
}

const applications = {
    AssignUsersToWorkflowStages,
    Comments,
    DashboardWidget,
    ErrorBoundary,
    GroupDetail,
    GroupsList,
    NextWorkflowStageButton,
    NotificationSettings,
    NotificationsDrawer,
    Provider,
    UserAccount,
    UserList,
    WorkflowEditor,
    WorkflowQuickLink,
    WorkflowTasks,
    WorkflowWidget,
    OpenDocumentSettingsButton,
    MaintenanceEditor
};

const appManagerInstance = new AppManager();

export const appMngmt = {
    add: appManagerInstance.add,
    remove: appManagerInstance.remove,
    update: appManagerInstance.update,
};

function reducer(state, action) {
    const {
        id, el, appType, namespace, options, context
    } = action;
    const app = state[id];
    switch (action.type) {
    case "ADD":
        if (app) {
            throw new Error(`Duplicate App-Id ${id}`);
        }
        return {
            [id]: {
                appType,
                context,
                namespace,
                el,
                id,
                options
            },
            ...state
        };
    case "REMOVE": {
        const copy = { ...state };
        delete copy[id];
        return copy;
    }
    case "UPSERT":
    case "UPDATE":
        return {
            ...state,
            [id]: {
                appType,
                context,
                namespace,
                el,
                id,
                options
            },
        };
    default:
        throw new Error();
    }
}

function isElement(obj) {
    try {
        //Using W3 DOM2 (works for FF, Opera and Chrome)
        return obj instanceof HTMLElement;
    }
    catch (e) {
        //Browsers not supporting W3 DOM2 don't have HTMLElement and
        //an exception is thrown and we end up here. Testing some
        //properties that all elements have (works on IE7)
        return (typeof obj === "object") &&
            (obj.nodeType === 1) && (typeof obj.style === "object") &&
            (typeof obj.ownerDocument === "object");
    }
}

const root = "v3RootClass";

/* eslint-disable react/prop-types */
const ApplicationManager = ({ source }) => {
    const [ apps, dispatch ] = React.useReducer(reducer, {});
    React.useEffect(
        () => {
            const handler = evt => dispatch(evt);
            source.addEventListener("ADD", handler, {});
            source.addEventListener("REMOVE", handler, {});
            source.addEventListener("UPDATE", handler, {});
            source.addEventListener("UPSERT", handler, {});
            return () => {
                source.removeEventListener("ADD", handler, {});
                source.removeEventListener("REMOVE", handler, {});
                source.removeEventListener("UPDATE", handler, {});
                source.removeEventListener("UPSERT", handler, {});
            };
        },
        [source],
    );

    return <>{Object.values(apps).map(({
        id, el, appType, options = {}, context = {}, namespace
    }) => {
        const Application = (applications[appType]);
        if (!el || !isElement(el)) {
            const errorMsg = `Can't initiallise app ${id} (${appType}) because root-dom-element 'el' of this application is ${el}`;
            console.warn(errorMsg);
            return <span data-error={errorMsg} />;
        }
        return <ErrorBoundary className={root} key={`root_${id}`}>{ReactDOM.createPortal(
            <ContextWrapper context={context}>
                <div className={`${root} chakra-portal`}>
                    <Application key={id} id={namespace || id} mdContext={context} {...options} />
                </div>
            </ContextWrapper>,
            el,
        )}</ErrorBoundary>;
    })}</>;
};
/* eslint-enable react/prop-types */

const elem = document.createElement("div");
document.body.appendChild(elem);
ReactDOM.render(
    <div className={root}>
        <Provider rootSelector={`.${root},.chakra-portal`}>
            <ApplicationManager source={appManagerInstance} />
            <LoginModal />
        </Provider>
    </div>,
    elem
);

export { store };
export default {
    store: Provider.store,
    appMngmt
};
